import React, { useCallback, useState } from "react";
import "./CrosswordGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { crosswordBestScoreKey, readBestScore, recordScore } from "../Quiz/bestScore.ts";
import { track } from "../../Shared/analytics.ts";
import { CrosswordGameRun } from "./CrosswordGameRun.tsx";
import { CrosswordGameSummary } from "./CrosswordGameSummary.tsx";
import { Plate, Silhouette } from "./CrosswordArt.tsx";
import { CROSSWORD_RANKS, CrosswordRank, CrosswordResult } from "./crosswordGame.ts";
import { readCrosswordRank, saveCrosswordRank } from "./crosswordStore.ts";
import { crosswordGameEventParams } from "./gameEventParams.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface CrosswordGamePageProps {
    slug: string;
    /** The route's `quiz`: the operation the calmer quiz is found by, since the lines add and take away (ADR 0134 §6). */
    operation: Operations;
}

interface Summary {
    result: CrosswordResult;
    best: number | null;
    isNewBest: boolean;
}

/** What each rank's end screen sends the child to practise, by slug (ADR 0134 §5). */
const PRACTICE: Record<CrosswordRank, string[]> = {
    constable: ["/addition-and-subtraction-within-20", "/missing-addend-worksheets", "/fact-family-worksheets"],
    inspector: ["/missing-number-worksheets", "/multiplication-and-division-fact-families"],
    detective: ["/order-of-operations-worksheets", "/missing-number-worksheets"],
};

/** Each rank's name, grades and note. `labelKey` so StringKeys.test.js sees the keys, as QuizPage's modes do. */
const RANK_LABELS: Record<CrosswordRank, { labelKey: StringKey }> = {
    constable: { labelKey: "flashCards.crosswordRankConstable" },
    inspector: { labelKey: "flashCards.crosswordRankInspector" },
    detective: { labelKey: "flashCards.crosswordRankDetective" },
};

const RANK_GRADES: Record<CrosswordRank, { labelKey: StringKey }> = {
    constable: { labelKey: "flashCards.crosswordRankGradesConstable" },
    inspector: { labelKey: "flashCards.crosswordRankGradesInspector" },
    detective: { labelKey: "flashCards.crosswordRankGradesDetective" },
};

const RANK_NOTES: Record<CrosswordRank, { labelKey: StringKey }> = {
    constable: { labelKey: "flashCards.crosswordRankNoteConstable" },
    inspector: { labelKey: "flashCards.crosswordRankNoteInspector" },
    detective: { labelKey: "flashCards.crosswordRankNoteDetective" },
};

const bestKeys = (): Record<string, number | null> => {
    const all: Record<string, number | null> = {};
    for (const rank of CROSSWORD_RANKS) {
        const key = crosswordBestScoreKey(rank);
        all[key] = readBestScore(key);
    }
    return all;
};

/**
 * The Baker Street Math Crossword (ADR 0134): a keyword landing page around a casebook of
 * five equation crosswords, with three rank buttons as Factor Merge has levels (ADR 0128).
 * A quiz route carrying `game: "crossword"`; its `quiz` is only the operation the link to
 * the calmer quiz is found by, since the game has no queue, picker, settings or missed
 * facts to wire.
 */
export function CrosswordGamePage({ slug, operation }: CrosswordGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [rank, setRank] = useState<CrosswordRank>(readCrosswordRank);
    const [playing, setPlaying] = useState(false);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [bests, setBests] = useState<Record<string, number | null>>(bestKeys);

    const plainQuiz = quizRoutes().find((route) => route.quiz === operation && !route.game);
    const best = bests[crosswordBestScoreKey(rank)] ?? null;

    function start() {
        setSummary(null);
        setPlaying(true);
        setRunId((id) => id + 1);
    }

    function chooseRank(next: CrosswordRank) {
        if (next === rank) {
            return;
        }
        setRank(next);
        saveCrosswordRank(next);
        setSummary(null);
    }

    const handleFinish = useCallback((result: CrosswordResult) => {
        const key = crosswordBestScoreKey(result.rank);
        const previous = readBestScore(key);
        const recorded = recordScore(key, result.glasses);
        // After the store, as the quiz does: analytics never costs a best score.
        track("quiz_completed", crosswordGameEventParams(result));
        setBests((all) => ({ ...all, [key]: recorded > 0 ? recorded : null }));
        setSummary({
            result,
            best: recorded > 0 ? recorded : null,
            isNewBest: previous !== null ? result.glasses > previous : result.glasses > 0,
        });
        setPlaying(false);
    }, []);

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="crosswordTool">
                <div className="cwDesk">
                    {playing ? (
                        <CrosswordGameRun key={runId} rank={rank} onFinish={handleFinish} />
                    ) : (
                        <>
                            <div className="cwTop">
                                <Plate />
                            </div>
                            {summary && (
                                <CrosswordGameSummary
                                    result={summary.result}
                                    best={summary.best}
                                    isNewBest={summary.isNewBest}
                                    practiceSlugs={PRACTICE[summary.result.rank]}
                                    onPlayAgain={() => {
                                        setRank(summary.result.rank);
                                        start();
                                    }}
                                />
                            )}

                            <section className="cwFile cwMenu">
                                <div className="cwHead">
                                    <Silhouette />
                                    <p className="cwHow">{t("flashCards.crosswordHowToPlay")}</p>
                                </div>

                                <div className="cwGroup" role="group" aria-label={t("flashCards.crosswordRanksLabel")}>
                                    {CROSSWORD_RANKS.map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            className="cwChoice"
                                            aria-pressed={option === rank}
                                            onClick={() => chooseRank(option)}
                                        >
                                            <span>{t(RANK_LABELS[option].labelKey)}</span>
                                            <span className="cwChoiceNote">{t(RANK_GRADES[option].labelKey)}</span>
                                        </button>
                                    ))}
                                </div>
                                <p className="cwRankNote">{t(RANK_NOTES[rank].labelKey)}</p>

                                <p className="cwBest">{best !== null ? format(t("flashCards.yourBestTemplate"), { n: best }) : ""}</p>

                                <button type="button" className="btn btn-primary cwStart" onClick={start}>
                                    {t("flashCards.crosswordStart")}
                                </button>

                                {plainQuiz && (
                                    <p className="cwCalmer">
                                        {t("flashCards.crosswordCalmerPrefix")}
                                        <a href={plainQuiz.slug}>{t("flashCards.crosswordCalmerLink")}</a>
                                        {t("flashCards.crosswordCalmerSuffix")}
                                    </p>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default CrosswordGamePage;
